import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { captureException } from '@/utils/sentry';
import {
  CheckIn_CheckInItemIssuesFieldsFragment,
  CheckIn_CheckInItemsFieldsFragment,
  CheckIn_CheckInItemsQuery,
  CheckIn_CheckInItemsQueryVariables,
  CheckIn_CompleteOrderInvoiceCheckInMutation,
  CheckIn_CompleteOrderInvoiceCheckInMutationVariables,
  CheckIn_OrderInvoiceItemsMutation,
  CheckIn_OrderInvoiceItemsMutationVariables,
  CheckInOrderInvoiceItemQuantityInput,
} from '@/types';
import {
  CheckInContextType,
  CheckInFilters,
  CheckInItem,
  CheckIssuesAmountMap,
  CheckItemQuantityMap,
  FilteredCheckInItemUUIDMap,
} from './check-in-context-types';
import { DataProxy, gql, useMutation } from '@apollo/client';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import {
  exactMatchFn,
  partialMatchFn,
  trackScanningEvent,
} from '@/utils/scanning';
import { getOperationName } from 'apollo-utilities';
import { OnDeviceScanningSearchType } from './dynamic-config';
import {
  ScannerType,
  ScanningEventSource,
  ScanningSearchMethod,
  ScanSound,
} from '@/components/scanner/scanner-types';
import { Type } from '@/components/toast';
import { useAdaptVariantPackToItemListOption } from './item-context-utils';
import { useAuthenticatedUserContext } from './user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { usePlayScanSounds } from '@/hooks/use-play-scan-sounds';
import { useToasts } from './toast-context';
import { useVariantPackInfoContext } from './variant-pack-info-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from './items-context-queries';

enum OrderInvoiceCheckInErrorCode {
  InvalidQuantity = 'INVALID_QUANTITY',
}

export const ORDER_INVOICE_LINE_ITEMS_FRAGMENT = gql`
  fragment CheckIn_OrderInvoiceLineItemsFields on order_invoice_line_items {
    item_code
    item_name
    ship_quantity
    unit_price_in_micros
    uuid
    variant_pack {
      ...ItemsContext_VariantsPackFields
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const ORDER_INVOICE_CHECK_IN_ITEM_ISSUES_FRAGMENT = gql`
  fragment CheckIn_CheckInItemIssuesFields on order_invoice_check_in_item_issues {
    quantity
    status
    uuid
  }
`;

export const ORDER_INVOICE_CHECK_IN_ITEMS_FRAGMENT = gql`
  fragment CheckIn_CheckInItemsFields on order_invoice_check_in_items {
    order_invoice_line_item {
      ...CheckIn_OrderInvoiceLineItemsFields
    }
    order_invoice_check_in_item_issues {
      ...CheckIn_CheckInItemIssuesFields
    }
    order_invoice_check_in_uuid
    quantity
    uuid
  }

  ${ORDER_INVOICE_LINE_ITEMS_FRAGMENT}
  ${ORDER_INVOICE_CHECK_IN_ITEM_ISSUES_FRAGMENT}
`;

export const GET_ORDER_INVOICE_CHECK_IN_QUERY = gql`
  query CheckIn_CheckInItems(
    $checkInUUID: uuid!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    order_invoice_check_ins_by_pk(uuid: $checkInUUID) {
      order_invoice_check_in_items(
        order_by: { order_invoice_line_item: { item_code: asc } }
      ) {
        ...CheckIn_CheckInItemsFields
      }
    }
  }

  ${ORDER_INVOICE_CHECK_IN_ITEMS_FRAGMENT}
`;

export const CHECK_IN_ORDER_INVOICE_ITEMS_MUTATION = gql`
  mutation CheckIn_OrderInvoiceItems(
    $checkInUUID: uuid!
    $orderInvoiceCheckInItems: [CheckInOrderInvoiceItemQuantityInput!]!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    checkInOrderInvoiceItems(
      order_invoice_check_in_id: $checkInUUID
      order_invoice_check_in_items: $orderInvoiceCheckInItems
    ) {
      order_invoice_check_in_item {
        ...CheckIn_CheckInItemsFields
      }
    }
  }

  ${ORDER_INVOICE_CHECK_IN_ITEMS_FRAGMENT}
`;

export const COMPLETE_ORDER_INVOICE_CHECK_IN_MUTATION = gql`
  mutation CheckIn_CompleteOrderInvoiceCheckIn($checkInUUID: uuid!) {
    completeOrderInvoiceCheckIn(order_invoice_check_in_id: $checkInUUID) {
      success
    }
  }
`;

export const CheckInContext = createContext<CheckInContextType | null>(null);

interface Props {
  checkInUUID: string;
  children?: React.ReactNode;
}

const getTotalAmountOfIssues = (
  issues: CheckIn_CheckInItemIssuesFieldsFragment[],
) =>
  issues.reduce((acc, { quantity }) => {
    acc += quantity;
    return acc;
  }, 0);

const getPartialMatchSearchableFields = (
  item: CheckIn_CheckInItemsFieldsFragment,
) => {
  const variantPack = item.order_invoice_line_item.variant_pack;
  return [
    item.order_invoice_line_item.item_name ?? '',
    variantPack?.item.display_name ?? '',
  ];
};

export const getSearchableFields = (
  item: CheckIn_CheckInItemsFieldsFragment,
) => {
  const variantPack = item.order_invoice_line_item.variant_pack;
  const upcList = variantPack?.upc_list ?? [];
  return [
    item.order_invoice_line_item.item_code ?? '',
    variantPack?.external_item_id ?? '',
    variantPack?.item.category ?? '',
    variantPack?.item.description ?? '',
    variantPack?.item.extra_keywords ?? '',
    ...upcList.filter(Boolean),
  ];
};

export const isMatchingFn = (
  code: string,
  matchType: OnDeviceScanningSearchType,
  item: CheckIn_CheckInItemsFieldsFragment,
) => {
  const matchingFields = getSearchableFields(item);
  const partialMatchingFields = getPartialMatchSearchableFields(item);

  const isMatching =
    matchType === OnDeviceScanningSearchType.EXACT_MATCH
      ? exactMatchFn(code, matchingFields)
      : partialMatchFn(code, matchingFields);

  const isPartialMatching = partialMatchFn(code, partialMatchingFields);

  return isMatching || isPartialMatching;
};

export const CheckInContextProvider: React.FC<Props> = ({
  checkInUUID,
  children,
}) => {
  const [filteredIdsMap, setFilteredIdsMap] =
    useState<FilteredCheckInItemUUIDMap>({
      [CheckInFilters.ALL]: new Set(),
      [CheckInFilters.FULLY_RECEIVED]: new Set(),
      [CheckInFilters.HAS_ISSUES]: new Set(),
      [CheckInFilters.NOT_FULFILLED]: new Set(),
      [CheckInFilters.PARTIAL_OR_MISSING]: new Set(),
    });
  const [itemQuantityMap, setItemQuantityMap] = useState<CheckItemQuantityMap>(
    {},
  );
  const [issuesAmountMap, setIssuesAmountMap] = useState<CheckIssuesAmountMap>(
    {},
  );
  const [selectedFilter, setSelectedFilter] = useState(
    CheckInFilters.PARTIAL_OR_MISSING,
  );
  const [searchResults, setSearchResults] = useState<
    CheckIn_CheckInItemsFieldsFragment[] | null
  >(null);

  /*
   * moved playScanSound to the context to avoid an issue with not playing sound
   * on initial mount of the OrderCheckInSingleItemScreen
   * */
  const playScanSound = usePlayScanSounds();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const { getVariantPackInfo } = useVariantPackInfoContext();
  const { showToast } = useToasts();
  const locale = useItemQueryLocale();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const onDeviceScanningConfig = useDynamicConfigValue(
    'on_device_scanning_config',
  );

  const checkInQueryVariables = useMemo(
    () => ({
      checkInUUID,
      locale,
      restaurantUUID,
      skipGroupMemberships: !itemGroupsEnabled,
      skipLiveSalt: !saltApiIntegrationEnabled,
      skipSaltApi: !saltApiIntegrationEnabled,
      useSaltCache: false,
    }),
    [
      checkInUUID,
      itemGroupsEnabled,
      locale,
      restaurantUUID,
      saltApiIntegrationEnabled,
    ],
  );

  const {
    data,
    loading,
    refetch: refetchCheckInQuery,
  } = useWrappedQuery<
    CheckIn_CheckInItemsQuery,
    CheckIn_CheckInItemsQueryVariables
  >(GET_ORDER_INVOICE_CHECK_IN_QUERY, {
    fetchPolicy: 'network-only',
    variables: checkInQueryVariables,
  });

  const allCheckInItems = useMemo(
    () => data?.order_invoice_check_ins_by_pk?.order_invoice_check_in_items,
    [data],
  );

  const shippedItems = useMemo(
    () =>
      allCheckInItems?.filter(
        (item) => item.order_invoice_line_item.ship_quantity >= 0,
      ),
    [allCheckInItems],
  );

  const filteredCheckInItems = useMemo(
    () =>
      shippedItems?.filter((item) =>
        filteredIdsMap[selectedFilter].has(item.uuid),
      ),
    [filteredIdsMap, selectedFilter, shippedItems],
  );

  const itemsShipQuantityMap = useMemo(() => {
    const newQuantityMap = new Map<string, number>();
    allCheckInItems?.forEach((item) => {
      newQuantityMap.set(item.uuid, item.order_invoice_line_item.ship_quantity);
    });
    return newQuantityMap;
  }, [allCheckInItems]);

  const orderInvoiceCheckInItems: CheckInOrderInvoiceItemQuantityInput[] =
    useMemo(
      () =>
        Object.keys(itemQuantityMap).reduce<
          CheckInOrderInvoiceItemQuantityInput[]
        >((acc, id) => {
          const initialItemQuantity = allCheckInItems?.find(
            (item) => item.uuid === id,
          )?.quantity;
          const currentItemQuantity = itemQuantityMap[id];

          if (
            currentItemQuantity !== undefined &&
            initialItemQuantity !== undefined &&
            currentItemQuantity !== initialItemQuantity
          ) {
            acc.push({
              final_quantity: currentItemQuantity,
              order_invoice_check_in_item_id: id,
              quantity: currentItemQuantity - initialItemQuantity,
            });
          }
          return acc;
        }, []),
      [allCheckInItems, itemQuantityMap],
    );

  const [checkInOrderInvoiceItems] = useMutation<
    CheckIn_OrderInvoiceItemsMutation,
    CheckIn_OrderInvoiceItemsMutationVariables
  >(CHECK_IN_ORDER_INVOICE_ITEMS_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [OrderInvoiceCheckInErrorCode.InvalidQuantity],
      retryEnabled: true,
    },
    variables: {
      ...checkInQueryVariables,
      orderInvoiceCheckInItems,
    },
  });

  const [completeCheckIn] = useMutation<
    CheckIn_CompleteOrderInvoiceCheckInMutation,
    CheckIn_CompleteOrderInvoiceCheckInMutationVariables
  >(COMPLETE_ORDER_INVOICE_CHECK_IN_MUTATION, {
    context: {
      retryEnabled: true,
    },
    refetchQueries: [
      getOperationName(GET_ORDER_INVOICE_CHECK_IN_QUERY) as string,
    ],
    variables: {
      checkInUUID,
    },
  });

  useEffect(() => {
    if (!loading && allCheckInItems?.length) {
      setFilteredIdsMap((currentFilteredIdsMap) => {
        const updatedQuantityMap =
          allCheckInItems.reduce<FilteredCheckInItemUUIDMap>(
            (acc, item) => {
              const {
                order_invoice_line_item: { ship_quantity },
                order_invoice_check_in_item_issues,
                quantity,
                uuid,
              } = item;

              const amountOfIssues = getTotalAmountOfIssues(
                order_invoice_check_in_item_issues,
              );

              if (ship_quantity > 0) {
                acc[CheckInFilters.ALL].add(uuid);
                if (amountOfIssues > 0) {
                  acc[CheckInFilters.HAS_ISSUES].add(uuid);
                }
                if (quantity < ship_quantity) {
                  acc[CheckInFilters.PARTIAL_OR_MISSING].add(uuid);
                } else if (quantity === ship_quantity) {
                  acc[CheckInFilters.FULLY_RECEIVED].add(uuid);
                }
              } else if (ship_quantity === 0) {
                acc[CheckInFilters.NOT_FULFILLED].add(uuid);
              }

              return acc;
            },
            {
              [CheckInFilters.ALL]: new Set(),
              [CheckInFilters.FULLY_RECEIVED]: new Set(),
              [CheckInFilters.HAS_ISSUES]: new Set(),
              [CheckInFilters.NOT_FULFILLED]: new Set(),
              [CheckInFilters.PARTIAL_OR_MISSING]: new Set(),
            },
          );

        return {
          ...currentFilteredIdsMap,
          ...updatedQuantityMap,
        };
      });
      setItemQuantityMap(
        allCheckInItems.reduce<CheckItemQuantityMap>(
          (acc, { uuid, quantity }) => ({
            ...acc,
            [uuid]: quantity,
          }),
          {},
        ),
      );
      setIssuesAmountMap(
        allCheckInItems.reduce<CheckItemQuantityMap>(
          (acc, { uuid, order_invoice_check_in_item_issues }) => ({
            ...acc,
            [uuid]: getTotalAmountOfIssues(order_invoice_check_in_item_issues),
          }),
          {},
        ),
      );
    }
  }, [allCheckInItems, loading]);

  const problematicItemsMetrics = useMemo(
    () =>
      shippedItems?.reduce(
        (acc, item) => {
          const problematicItems = new Set([
            ...filteredIdsMap.PARTIAL_OR_MISSING,
            ...filteredIdsMap.HAS_ISSUES,
          ]);
          if (problematicItems.has(item.uuid)) {
            const amountOfIssues = issuesAmountMap[item.uuid];
            const missingItems =
              itemsShipQuantityMap.get(item.uuid)! - itemQuantityMap[item.uuid];
            const totalProblematicQuantity = amountOfIssues + missingItems;
            const totalProblematicCost =
              item.order_invoice_line_item.unit_price_in_micros;
            acc.missing += missingItems;
            acc.withIssues += amountOfIssues;
            acc.cost += totalProblematicCost * totalProblematicQuantity;
          }
          return acc;
        },
        {
          cost: 0,
          missing: 0,
          withIssues: 0,
        },
      ),
    [
      filteredIdsMap.HAS_ISSUES,
      filteredIdsMap.PARTIAL_OR_MISSING,
      issuesAmountMap,
      itemQuantityMap,
      itemsShipQuantityMap,
      shippedItems,
    ],
  );

  const totalShippedQuantity = useMemo(() => {
    if (!shippedItems) {
      return 0;
    }
    return shippedItems.reduce((acc, item) => {
      const itemShippedQuantity = itemsShipQuantityMap.get(item.uuid) ?? 0;
      acc += itemShippedQuantity;
      return acc;
    }, 0);
  }, [itemsShipQuantityMap, shippedItems]);

  const saveCheckInChanges = useCallback(async () => {
    if (orderInvoiceCheckInItems.length) {
      await checkInOrderInvoiceItems();
    }
  }, [checkInOrderInvoiceItems, orderInvoiceCheckInItems]);

  const onItemQuantityUpdate = useCallback(
    (itemUUID: string, quantity: number) => {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      setItemQuantityMap((currentItemQuantityMap) => {
        return {
          ...currentItemQuantityMap,
          [itemUUID]: quantity,
        };
      });
      if (
        quantity === itemsShipQuantityMap.get(itemUUID) ||
        filteredIdsMap[CheckInFilters.FULLY_RECEIVED].has(itemUUID)
      ) {
        setFilteredIdsMap((currentFilteredIdsMap) => {
          const updatedFullyReceivedSet = new Set(
            currentFilteredIdsMap[CheckInFilters.FULLY_RECEIVED],
          );
          const updatedPartialOrMissingSet = new Set(
            currentFilteredIdsMap[CheckInFilters.PARTIAL_OR_MISSING],
          );
          if (quantity === itemsShipQuantityMap.get(itemUUID)) {
            // move the item from PARTIAL_OR_MISSING to FULLY_RECEIVED
            updatedFullyReceivedSet.add(itemUUID);
            updatedPartialOrMissingSet.delete(itemUUID);
          } else {
            // move the item from FULLY_RECEIVED to PARTIAL_OR_MISSING
            updatedFullyReceivedSet.delete(itemUUID);
            updatedPartialOrMissingSet.add(itemUUID);
          }
          return {
            ...currentFilteredIdsMap,
            [CheckInFilters.FULLY_RECEIVED]: updatedFullyReceivedSet,
            [CheckInFilters.PARTIAL_OR_MISSING]: updatedPartialOrMissingSet,
          };
        });
      }
    },
    [filteredIdsMap, itemsShipQuantityMap],
  );

  const refreshOnReportIssue = useCallback(
    (
      client: DataProxy,
      checkInItemUUID: string,
      resultCheckInItem?: CheckIn_CheckInItemsFieldsFragment | null,
    ) => {
      try {
        const checkInItemsQuery = client.readQuery<
          CheckIn_CheckInItemsQuery,
          CheckIn_CheckInItemsQueryVariables
        >({
          query: GET_ORDER_INVOICE_CHECK_IN_QUERY,
          variables: checkInQueryVariables,
        });

        if (!checkInItemsQuery?.order_invoice_check_ins_by_pk) {
          return;
        }

        const updatedCheckInItems =
          checkInItemsQuery.order_invoice_check_ins_by_pk.order_invoice_check_in_items.map(
            (cachedCheckInItem) => {
              if (
                cachedCheckInItem.uuid === checkInItemUUID &&
                resultCheckInItem
              ) {
                return resultCheckInItem;
              }
              return cachedCheckInItem;
            },
          );

        if (checkInItemsQuery && resultCheckInItem) {
          client.writeQuery<CheckIn_CheckInItemsQuery>({
            data: {
              ...checkInItemsQuery,
              order_invoice_check_ins_by_pk: {
                ...checkInItemsQuery.order_invoice_check_ins_by_pk,
                order_invoice_check_in_items: updatedCheckInItems,
              },
            },
            query: GET_ORDER_INVOICE_CHECK_IN_QUERY,
            variables: checkInQueryVariables,
          });
        }
      } catch (e) {
        captureException(e);
      }
    },
    [checkInQueryVariables],
  );
  const refreshOnDeleteIssue = useCallback(
    (client: DataProxy, checkInItemUUID: string, issueUUIDs: string[]) => {
      try {
        const checkInItemsQuery = client.readQuery<
          CheckIn_CheckInItemsQuery,
          CheckIn_CheckInItemsQueryVariables
        >({
          query: GET_ORDER_INVOICE_CHECK_IN_QUERY,
          variables: checkInQueryVariables,
        });

        if (!checkInItemsQuery?.order_invoice_check_ins_by_pk) {
          return;
        }

        const updatedCheckInItems =
          checkInItemsQuery.order_invoice_check_ins_by_pk.order_invoice_check_in_items.map(
            (item) => {
              if (item.uuid === checkInItemUUID) {
                const updatedIssues =
                  item.order_invoice_check_in_item_issues.filter(
                    (issue) => !issueUUIDs.includes(issue.uuid),
                  );
                return {
                  ...item,
                  order_invoice_check_in_item_issues: updatedIssues,
                };
              }
              return item;
            },
          );

        if (checkInItemsQuery) {
          client.writeQuery<CheckIn_CheckInItemsQuery>({
            data: {
              ...checkInItemsQuery,
              order_invoice_check_ins_by_pk: {
                ...checkInItemsQuery.order_invoice_check_ins_by_pk,
                order_invoice_check_in_items: updatedCheckInItems,
              },
            },
            query: GET_ORDER_INVOICE_CHECK_IN_QUERY,
            variables: checkInQueryVariables,
          });
        }
      } catch (e) {
        captureException(e);
      }
    },
    [checkInQueryVariables],
  );

  const currencyCode = useMemo(() => {
    for (const item of allCheckInItems || []) {
      const variantPackUUID = item.order_invoice_line_item.variant_pack?.uuid;
      if (!variantPackUUID) {
        continue;
      }
      const variantPackInfo = getVariantPackInfo(variantPackUUID);
      if (variantPackInfo?.currencyCode) {
        return variantPackInfo.currencyCode;
      }
    }
    return DEFAULT_CURRENCY_CODE;
  }, [allCheckInItems, getVariantPackInfo]);

  const searchForItems = useCallback(
    (code: string, scannerType: ScannerType) => {
      if (shippedItems) {
        const filteredItems = shippedItems.filter((item) => {
          return isMatchingFn(
            code,
            onDeviceScanningConfig?.search_type ??
              OnDeviceScanningSearchType.EXACT_MATCH,
            item,
          );
        });

        trackScanningEvent({
          code,
          rawResults: filteredItems.map((vp) => vp.uuid),
          restaurantUUID,
          searchMethod: ScanningSearchMethod.OnDevice,
          source: ScanningEventSource.CheckIn,
          supplierUUID,
          type: scannerType,
        });

        if (filteredItems.length > 1) {
          playScanSound(ScanSound.MultipleResults);
        }

        setSearchResults(filteredItems);
        return filteredItems;
      }
      return [];
    },
    [
      onDeviceScanningConfig?.search_type,
      playScanSound,
      restaurantUUID,
      shippedItems,
      supplierUUID,
    ],
  );

  const clearSearchResults = useCallback(() => setSearchResults(null), []);

  const adaptCheckInItemFragment = useCallback(
    (checkInItemFragment: CheckIn_CheckInItemsFieldsFragment) => {
      const {
        order_invoice_check_in_item_issues: issues,
        order_invoice_check_in_uuid: checkInUUID,
        order_invoice_line_item: lineItem,
        uuid: checkInItemUUID,
      } = checkInItemFragment;

      const variantPack = lineItem.variant_pack
        ? adaptVariantPackToItemListOption(lineItem.variant_pack)
        : null;

      const variantPackInfo = variantPack
        ? getVariantPackInfo(variantPack.metadata!.variantPackUUID)
        : null;

      return {
        amountOfIssues: issuesAmountMap[checkInItemUUID],
        checkInItemUUID,
        checkInUUID: checkInUUID,
        currencyCode: variantPackInfo?.currencyCode ?? DEFAULT_CURRENCY_CODE,
        issues,
        manufacturerSuggestedRetailPriceInMicros:
          variantPackInfo?.manufacturerSuggestedRetailPriceInMicros,
        priceInMicros: lineItem.unit_price_in_micros,
        shipQuantity: lineItem.ship_quantity,
        suggestedRetailPriceInMicros:
          variantPackInfo?.suggestedRetailPriceInMicros,
        title: lineItem.item_name ?? '',
        variantPack,
      };
    },
    [adaptVariantPackToItemListOption, getVariantPackInfo, issuesAmountMap],
  );

  const autoIncrementQuantity = useCallback(
    (checkInItem: CheckInItem) => {
      const { checkInItemUUID, shipQuantity } = checkInItem;
      const quantity = itemQuantityMap[checkInItemUUID];
      const newQuantity = quantity + 1;

      if (quantity !== shipQuantity) {
        if (newQuantity === shipQuantity) {
          playScanSound(ScanSound.MaxQuantity);
        } else if (quantity === 0) {
          playScanSound(ScanSound.IncrementFirst);
        } else {
          playScanSound(ScanSound.IncrementSubsequent);
        }
        onItemQuantityUpdate(checkInItemUUID, newQuantity);
      } else {
        showToast({
          content: i18n.t('check_in.max_quantity_reached'),
          type: Type.NEGATIVE,
        });
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
        playScanSound(ScanSound.Unavailable);
      }
    },
    [itemQuantityMap, onItemQuantityUpdate, playScanSound, showToast],
  );

  const value = useMemo(
    () => ({
      adaptCheckInItemFragment,
      autoIncrementQuantity,
      clearSearchResults,
      completeCheckIn,
      currencyCode,
      filteredCheckInItems,
      filteredIdsMap,
      itemQuantityMap,
      loading,
      onItemQuantityUpdate,
      playScanSound,
      problematicItemsMetrics,
      refetchCheckInQuery,
      refreshOnDeleteIssue,
      refreshOnReportIssue,
      saveCheckInChanges,
      searchForItems,
      searchResults,
      selectedFilter,
      setSelectedFilter,
      shippedItems,
      totalShippedQuantity,
    }),
    [
      adaptCheckInItemFragment,
      autoIncrementQuantity,
      clearSearchResults,
      completeCheckIn,
      currencyCode,
      filteredCheckInItems,
      filteredIdsMap,
      itemQuantityMap,
      loading,
      onItemQuantityUpdate,
      playScanSound,
      problematicItemsMetrics,
      refetchCheckInQuery,
      refreshOnDeleteIssue,
      refreshOnReportIssue,
      saveCheckInChanges,
      searchForItems,
      searchResults,
      selectedFilter,
      shippedItems,
      totalShippedQuantity,
    ],
  );
  return (
    <CheckInContext.Provider value={value}>{children}</CheckInContext.Provider>
  );
};

export const useCheckInContext = () => {
  const context = useContext(CheckInContext);
  if (!context) {
    throw new Error('useCheckInContext requires a CheckInContextProvider');
  }

  return context;
};
